import React from 'react';
import styles from './index.module.less';
import CompHeaderLogo from './components/CompHeaderLogo';
import CompHeaderNav from './components/CompHeaderNav';
import type { IScene, ITheme, IThemeConfig } from '@reolink-web/components.basic';
import { ThemeConfigContext } from '@reolink-web/components.basic';
import '@reolink-web/components.basic/dist/style.css';
import CompHeaderAction from './components/CompHeaderAction';
import type { IHeaderContext } from './common';
import { HeaderContext } from './common';
import { getUserProfile } from '@/api/client/account';
import type { IUserProfileRes } from '@/interface/account/IGetUserProfile';
import * as COMMON from '@/common';
import type { IMultiSiteStoreFetchRes } from '@/reolink/system-pages/multi-site-page/fetch';
import ReoI18n, { useTranslation } from '@/modules/ReoI18n';
import i18n from 'i18next';
import eventBus from '@/utils/event';
import { langCodeTransform } from '$/Components';
import { continentList } from '@/common';

const CompReolinkHeader: React.FC<{ ctx: IMultiSiteStoreFetchRes; }> = React.memo((props) => {
    const lang = props.ctx.lang ? langCodeTransform(props.ctx.lang) : 'en';
    const country = props.ctx.country ? props.ctx.country : '_';
    const reoI18n = React.useMemo(() => {
        const reoI18n = new ReoI18n();
        reoI18n.init({
            i18nInstance: i18n,
            currentLang: lang,
        });
        return reoI18n;
    }, [lang]);

    reoI18n.loadResource(
        ['relink-header'],
        {
            ...props.ctx.i18nPackage,
        },
        lang,
    );

    const { t } = useTranslation();

    const getTextByKey = React.useCallback(
        (key: string) => {
            const text = t(`reolink-header:${key}`);
            return text ? text : key;
        },
        [t],
    );

    const [isShowSearch, setIsShowSearch] = React.useState<boolean>(false);
    const [countryList, setCountryList] = React.useState<IHeaderContext['countryList']>([]);
    const [userPrefile, setUserPrefile] = React.useState<IHeaderContext['userPrefile']>();
    const countryTitle = React.useMemo(() => {
        // 未获取到countryList时标题为空
        if (!countryList.length) {
            return '';
        }

        for (const continent of countryList) {
            for (const countryItem of continent?.countryList ?? []) {
                if (countryItem.value === country) {
                    return countryItem.title;
                }
            }
        }
        return getTextByKey('other-regions');
    }, [country, getTextByKey, countryList]);
    const headerContextValue = React.useMemo<IHeaderContext>(() => {
        const langTitle = COMMON.supportLangMap[lang];
        eventBus.emit('countryLangChange', {
            lang: lang,
            country: props.ctx.country,
            countryTitle,
            langTitle,
            countryList: countryList,
            isDistributor: !!userPrefile?.distributor,
        });

        return {
            isShowSearch,
            setIsShowSearch,
            countryList,
            userPrefile: userPrefile,
            langList: COMMON.supportLangList,
            lang,
            country: props.ctx.country,
            getTextByKey,
            countryTitle,
            langTitle,
        };
    }, [countryList, countryTitle, getTextByKey, isShowSearch, lang, props.ctx.country, userPrefile]);

    const fetchUserPrefileRef = React.useRef(async () => {
        try {
            const res = await getUserProfile();
            setUserPrefile(res.me);
            const continentCountries: Record<string, IUserProfileRes['sellableCountries']> = {};

            for (const item of res.sellableCountries) {
                continentCountries[item.continent] ??= [];
                continentCountries[item.continent].push(item);
            }

            const data = continentList.map((item) => {
                return {
                    continentCode: item.code,
                    continentTitle: item.name[props.ctx.lang] ?? item.name.en,
                    countryList:
                        continentCountries[item.code]
                            ?.map((item) => {
                                const langKey = item.language[0];
                                const currency = `${item.currency.name}(${item.currency.symbol})`;
                                const lang = langKey ? COMMON.supportLangMap[langKey] : COMMON.supportLangMap['en'];
                                return {
                                    value: item.country,
                                    title: item.countryName,
                                    lang: lang,
                                    langKey,
                                    currency,
                                };
                            })
                            .sort((a, b) => {
                                return a.title.localeCompare(b.title, props.ctx.lang, { sensitivity: 'base' });
                            }) ?? [],
                };
            });
            setCountryList(data);
        }
        catch (e) {
            console.error(e);
        }
    });

    const [scene, setScene] = React.useState<IScene>('reolink');
    const [theme, setTheme] = React.useState<ITheme>('light');
    const setSceneCallback = React.useCallback((scene: IScene): void => {
        setScene(scene);
    }, []);
    const setThemeCallback = React.useCallback((theme: ITheme): void => {
        setTheme(theme);
    }, []);
    const themeConfig = React.useMemo((): IThemeConfig => {
        return {
            scene,
            theme,
            setScene: setSceneCallback,
            setTheme: setThemeCallback,
        };
    }, [scene, theme, setSceneCallback, setThemeCallback]);

    React.useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        fetchUserPrefileRef.current();
    }, []);

    return (
        <ThemeConfigContext.Provider value={ themeConfig }>
            <HeaderContext.Provider value={ headerContextValue }>
                <div className={ styles['header-container'] }>
                    <div className={ styles['header-wrapper'] }>
                        <div className={ styles['header-logo'] }>
                            <CompHeaderLogo />
                        </div>
                        <div className={ styles['header-nav'] }>
                            <CompHeaderNav />
                        </div>
                        <div className={ styles['header-mobile-logo'] }>
                            <CompHeaderLogo />
                        </div>
                        <div className={ styles['header-action'] }>
                            <CompHeaderAction />
                        </div>
                    </div>
                </div>
            </HeaderContext.Provider>
        </ThemeConfigContext.Provider>
    );
});
export default CompReolinkHeader;
